import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  createFitting,
  deleteFitting,
  fetchFitting,
  fetchFittings,
  FittingDetail,
  FittingStatus,
  FittingSummary,
  NewFittingTag,
  updateFittingShare,
  updateFittingStatus,
} from '../api/fittings';
import ConsolePanel from '../components/ConsolePanel';
import FittingWorkbench, { FittingOverviewBox } from '../components/FittingWorkbench';
import ShareScopeSelect from '../components/ShareScopeSelect';
import FittingStatusBadge from '../components/FittingStatusBadge';
import FittingTagEditor, { ACTIVITY_TAGS, WH_CLASS_TAGS } from '../components/FittingTagEditor';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from '../services/AuthContext';
import { C, FONT, frameSx, hexToRgba, lcarsLabelSx, prefersReducedMotion, revealSx } from '../theme/tokens';
import { ShareScope } from '../types';

/** The one-off "just landed" pulse for a freshly-imported row — the same
 * shape as the Data Vault's tracked-arrival signal (a scanner ring that
 * blooms once and fades), because the situation is the same one: something
 * appeared in a list the viewer is looking at without them having to
 * refresh anything, so it earns a real signal rather than just appearing
 * silently in the pile. */
function justImportedSx() {
  if (prefersReducedMotion()) {
    return { boxShadow: `0 0 0 1px ${hexToRgba(C.cyan, 0.45)}` } as const;
  }
  return {
    animation: 'fitJustImported 2s cubic-bezier(0.16,1,0.3,1) both',
    '@keyframes fitJustImported': {
      '0%': { boxShadow: `0 0 0 0 ${hexToRgba(C.cyan, 0.55)}`, borderColor: hexToRgba(C.cyan, 0.7) },
      '55%': { boxShadow: `0 0 18px 2px ${hexToRgba(C.cyan, 0.28)}` },
      '100%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
    },
  } as const;
}

/** EFT paste → parse/resolve/save → saved fittings list with computed
 * EHP/DPS/tank stats (server-side dogma port of poorBattlegrounds' fitting
 * engine — see src/services/fitting/fittingStats.ts).
 *
 * The list is every fitting you can SEE — your own plus every one shared to
 * you — not just your own, because "what does the corp fly here" is the
 * question this page exists to answer. Tagging is a member action now
 * (components/FittingTagEditor), so a fit reaches the site and class pages
 * without an admin in the loop.
 *
 * The page IS the library. It used to open on the import form, with the
 * saved fits behind a second tab — so the answer to "what does the corp
 * fly" was one click away from every arrival, and a page holding forty
 * shared fits greeted you with an empty textarea. Import is the secondary
 * action now, which is what it is: you browse this page far more often than
 * you paste into it. They stay separate tabs rather than stacking, because
 * pasting an EFT block and scanning a long list fought for the same scroll
 * position when they shared one. */
const Fittings: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(TAB_LIBRARY);
  const [name, setName] = useState('');
  const [eftText, setEftText] = useState('');
  const [importTags, setImportTags] = useState<NewFittingTag[]>([]);
  // Private by default — the same safe-first default the Data Vault's own
  // ShareScopeSelect leads with. Widening it is a deliberate choice made
  // right here on the way in, not a second trip through the dialog after.
  const [importShareScope, setImportShareScope] = useState<ShareScope>('private');
  const [importShareGroupId, setImportShareGroupId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [justImportedId, setJustImportedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A `?fit=<id>` link (from a linked-fittings chip elsewhere, or a saved
  // bookmark) opens straight into the popup, same as clicking a row — and
  // makes sure the library is behind it rather than the import form.
  useEffect(() => {
    const requested = searchParams.get('fit');
    if (requested) {
      setSelectedId(Number(requested));
      setTab(TAB_LIBRARY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data: fittings, isLoading } = useQuery({ queryKey: ['fittings'], queryFn: fetchFittings });

  const createMutation = useMutation({
    mutationFn: () => createFitting(name.trim(), eftText, importTags, importShareScope, importShareGroupId),
    onSuccess: ({ id }) => {
      setName('');
      setEftText('');
      setImportTags([]);
      setImportShareScope('private');
      setImportShareGroupId(null);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['fittings'] });
      // The tags just written are what the site and class pages read.
      queryClient.invalidateQueries({ queryKey: ['tag-vocabulary'] });
      queryClient.invalidateQueries({ queryKey: ['fitting-recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['fittings-by-class'] });
      queryClient.invalidateQueries({ queryKey: ['fittings-by-site'] });
      setSelectedId(id);
      setJustImportedId(id);
      setTab(TAB_LIBRARY);
      // The pulse is a one-time "this just arrived" signal, not a standing
      // decoration — clear the flag once it's had time to play so
      // revisiting the tab later doesn't replay it.
      setTimeout(() => setJustImportedId((cur) => (cur === id ? null : cur)), 2200);
    },
    onError: () => setError('Could not parse that EFT text — check the format (starts with "[Ship, Fit Name]").'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteFitting(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fittings'] });
      setSelectedId(null);
    },
  });

  return (
    <Stack spacing={2.5} sx={revealSx()}>
      <Typography variant="h1" sx={{ color: C.text, fontFamily: FONT.display, fontSize: '1.7rem' }}>
        Fittings
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab
          icon={<SearchIcon sx={{ fontSize: 17 }} />}
          iconPosition="start"
          label={`All Fittings${fittings && fittings.length > 0 ? ` (${fittings.length})` : ''}`}
        />
        <Tab icon={<UploadFileIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Import" />
      </Tabs>

      {/* Keyed on the tab index so the entrance replays on every switch —
          the one signal, past the content simply changing, that says "this
          is a different place" (same reasoning as Intel.tsx's own TabPanel). */}
      <Box key={tab} sx={revealSx()}>
        {tab === TAB_LIBRARY ? (
          <SavedFittingsTab
            fittings={fittings}
            isLoading={isLoading}
            selectedId={selectedId}
            justImportedId={justImportedId}
            onSelect={setSelectedId}
            onDelete={(id) => deleteMutation.mutate(id)}
            onGoToImport={() => setTab(TAB_IMPORT)}
          />
        ) : (
          <ImportTab
            name={name}
            eftText={eftText}
            tags={importTags}
            shareScope={importShareScope}
            shareGroupId={importShareGroupId}
            error={error}
            pending={createMutation.isPending}
            onNameChange={setName}
            onEftChange={setEftText}
            onTagsChange={setImportTags}
            onShareChange={(scope, groupId) => {
              setImportShareScope(scope);
              setImportShareGroupId(groupId);
            }}
            onImport={() => createMutation.mutate()}
          />
        )}
      </Box>

      <FittingDialog id={selectedId} onClose={() => setSelectedId(null)} />
    </Stack>
  );
};

/** Which tab is which. Named because the library is the landing tab now and
 * a bare `setTab(1)` three screens away is how that quietly gets reversed. */
const TAB_LIBRARY = 0;
const TAB_IMPORT = 1;

const ImportTab: React.FC<{
  name: string;
  eftText: string;
  tags: NewFittingTag[];
  shareScope: ShareScope;
  shareGroupId: number | null;
  error: string | null;
  pending: boolean;
  onNameChange: (v: string) => void;
  onEftChange: (v: string) => void;
  onTagsChange: (v: NewFittingTag[]) => void;
  onShareChange: (scope: ShareScope, groupId: number | null) => void;
  onImport: () => void;
}> = ({
  name,
  eftText,
  tags,
  shareScope,
  shareGroupId,
  error,
  pending,
  onNameChange,
  onEftChange,
  onTagsChange,
  onShareChange,
  onImport,
}) => {
  const has = (tagType: string, tagValue: string) =>
    tags.some((t) => t.tagType === tagType && t.tagValue === tagValue);
  const toggle = (tagType: string, tagValue: string) =>
    onTagsChange(
      has(tagType, tagValue)
        ? tags.filter((t) => !(t.tagType === tagType && t.tagValue === tagValue))
        : [...tags, { tagType, tagValue }],
    );

  return (
    <ConsolePanel title="Import EFT" accent={C.cyan}>
      <Stack spacing={1.5}>
        <TextField size="small" placeholder="Fitting name" value={name} onChange={(e) => onNameChange(e.target.value)} />
        <TextField
          multiline
          minRows={6}
          placeholder={'[Ship Name, Fit Name]\nModule 1\nModule 2\n\nMid Module\n\nHigh Module,Ammo Name\n\nRig 1\n\nDrone Name x5'}
          value={eftText}
          onChange={(e) => onEftChange(e.target.value)}
          sx={{ fontFamily: FONT.mono }}
        />

        {/* Tagging on the way in, not as a second trip.
            A fit's tags are what make it reachable — the class pages and
            site pages are built out of them — and "I'll tag it later" is a
            thing nobody does, so the two categories that matter are one
            click each right here. Everything more specific (a named site, a
            doctrine) is added on the fit itself afterwards. */}
        <Box>
          <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem'), mb: 0.75 }}>what is it for?</Typography>
          <Stack spacing={0.75}>
            <TagPickRow label="class" values={WH_CLASS_TAGS} tagType="wh_class" has={has} onToggle={toggle} />
            <TagPickRow label="activity" values={ACTIVITY_TAGS} tagType="activity" has={has} onToggle={toggle} />
          </Stack>
          <Typography sx={{ color: C.textDim, fontSize: '0.7rem', mt: 0.75 }}>
            Choose classes, activities, or both. Class + activity matches both. Tagging a private fit automatically
            shares it with your corp; existing sharing scopes keep their audience.
          </Typography>
        </Box>

        {/* Same scope, same options, as the Data Vault's own sharing —
            private/group/corp/alliance/public — but this one setting only
            ever governs THIS fitting, never anything else the uploader
            owns. Chosen here rather than left to a second trip through the
            saved fit's own settings dropdown, since "who should see this"
            is a decision made once, at the moment of pasting it in. */}
        <Box>
          <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem'), mb: 0.75 }}>who can see it?</Typography>
           <ShareScopeSelect value={tags.length > 0 && shareScope === 'private' ? 'corp' : shareScope} groupId={shareGroupId} onChange={onShareChange} />
        </Box>

        {error && <Typography sx={{ color: C.red, fontSize: '0.8rem' }}>{error}</Typography>}
        <Button
          variant="contained"
          color="secondary"
          disabled={!name.trim() || !eftText.trim() || pending}
          onClick={onImport}
          sx={{ alignSelf: 'flex-start' }}
        >
          {pending ? 'Parsing…' : 'Import'}
        </Button>
      </Stack>
    </ConsolePanel>
  );
};

/** One labelled row of toggle chips. Same pill as the library's filters on
 * purpose: you pick a tag here and click the identical thing over there to
 * find it again. */
const TagPickRow: React.FC<{
  label: string;
  values: string[];
  tagType: string;
  has: (tagType: string, tagValue: string) => boolean;
  onToggle: (tagType: string, tagValue: string) => void;
}> = ({ label, values, tagType, has, onToggle }) => (
  <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.58rem'), width: 72, flexShrink: 0 }}>{label}</Typography>
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
      {values.map((v) => (
        <FilterChip key={v} label={v} active={has(tagType, v)} onClick={() => onToggle(tagType, v)} />
      ))}
    </Stack>
  </Stack>
);

/** Every distinct tag value across the account's fittings, "All" prepended
 * — the filter row that lets "show me every C5-tagged fit" replace scrolling
 * a growing flat list by eye. Search and tag filter compose (both must
 * match), since "the Loki fits tagged C5" is exactly the kind of question
 * this page exists to answer fast. */
const SavedFittingsTab: React.FC<{
  fittings: FittingSummary[] | undefined;
  isLoading: boolean;
  selectedId: number | null;
  justImportedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onGoToImport: () => void;
}> = ({ fittings, isLoading, selectedId, justImportedId, onSelect, onDelete, onGoToImport }) => {
  const [query, setQuery] = useState('');
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [tagMatch, setTagMatch] = useState<'all' | 'any'>('all');
  const [owner, setOwner] = useState<OwnerFilter>('all');
  const [sort, setSort] = useState<SortKey>('recent');

  // Tags are grouped by their category so the filter row reads as
  // "C5 C6 | ratting gas huffing | ..." rather than one undifferentiated
  // alphabetical pile — with forty fits and several categories, a flat
  // sorted list buries the class tags among the activity ones.
  const tagGroups = useMemo(() => {
    const byType = new Map<string, Set<string>>();
    for (const f of fittings ?? []) {
      for (const t of f.tags) {
        if (!byType.has(t.tag_type)) byType.set(t.tag_type, new Set());
        byType.get(t.tag_type)!.add(t.tag_value);
      }
    }
    return [...byType.entries()]
      .map(([type, values]) => ({ type, values: [...values].sort((a, b) => a.localeCompare(b)) }))
      .sort((a, b) => a.type.localeCompare(b.type));
  }, [fittings]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = (fittings ?? []).filter((f) => {
      // Search reaches the tags too: typing "gas" should find the fit
      // tagged for gas huffing, not just one with "gas" in its name.
      const matchesQuery =
        !q ||
        f.name.toLowerCase().includes(q) ||
        f.shipName.toLowerCase().includes(q) ||
        f.tags.some((t) => t.tag_value.toLowerCase().includes(q) || t.tag_type.toLowerCase().includes(q));
      const hasTag = (key: string) => f.tags.some((t) => JSON.stringify([t.tag_type, t.tag_value]) === key);
      const matchesTag = !activeTags.length || (tagMatch === 'all' ? activeTags.every(hasTag) : activeTags.some(hasTag));
      const matchesOwner = owner === 'all' || (owner === 'mine' ? f.isMine : !f.isMine);
      return matchesQuery && matchesTag && matchesOwner;
    });
    // Sorting on a copy: `fittings` is react-query's cached array, and
    // sorting it in place mutates what every other consumer reads.
    return [...matching].sort(SORTS[sort].compare);
  }, [fittings, query, activeTags, tagMatch, owner, sort]);

  if (isLoading) {
    return (
      <ConsolePanel title="Saved Fittings" accent={C.cyan}>
        <CircularProgress size={20} sx={{ color: C.cyan }} />
      </ConsolePanel>
    );
  }

  if (!fittings || fittings.length === 0) {
    return (
      <ConsolePanel title="Saved Fittings" accent={C.cyan}>
        <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Typography sx={{ color: C.textDim }}>
            No fittings here yet — none of your own, and nothing shared with you.
          </Typography>
          <Button size="small" onClick={onGoToImport} sx={{ color: C.cyan }}>
            Import one &rarr;
          </Button>
        </Stack>
      </ConsolePanel>
    );
  }

  return (
    <ConsolePanel title="Saved Fittings" accent={C.cyan}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search name, ship or tag&hellip;"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: C.textDim }} />
                </InputAdornment>
              ),
            }}
          />
          {/* Sorting by tank or by damage is the comparison this page is
              for — "which of our C5 fits has the most buffer" is one pick
              here plus one tag chip, where it used to be opening each fit
              in turn and remembering the numbers. */}
          <TextField
            size="small"
            select
            label="Sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            sx={{ width: 170, flexShrink: 0 }}
          >
            {(Object.keys(SORTS) as SortKey[]).map((k) => (
              <MenuItem key={k} value={k}>
                {SORTS[k].label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            {OWNER_FILTERS.map((o) => (
              <FilterChip
                key={o.value}
                label={o.label}
                active={owner === o.value}
                onClick={() => setOwner(o.value)}
              />
            ))}
          </Stack>
        </Stack>

        {tagGroups.length > 0 && (
          <Stack spacing={0.75}>
            {tagGroups.map((group) => (
              <Stack key={group.type} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.58rem'), width: 72, flexShrink: 0 }}>
                  {group.type.replace(/_/g, ' ')}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                  {group.values.map((t) => (
                    <FilterChip
                      key={t}
                      label={t}
                       active={activeTags.includes(JSON.stringify([group.type, t]))}
                       onClick={() => {
                         const key = JSON.stringify([group.type, t]);
                         setActiveTags((current) => current.includes(key) ? current.filter((v) => v !== key) : [...current, key]);
                       }}
                    />
                  ))}
                </Stack>
              </Stack>
            ))}
             {activeTags.length > 0 && (
               <Stack direction="row" spacing={1} alignItems="center">
                 <FilterChip label="Match all (AND)" active={tagMatch === 'all'} onClick={() => setTagMatch('all')} />
                 <FilterChip label="Match any (OR)" active={tagMatch === 'any'} onClick={() => setTagMatch('any')} />
                 <Button size="small" onClick={() => setActiveTags([])} sx={{ fontSize: '0.7rem', color: C.textDim }}>
                   Clear tag filters
                 </Button>
               </Stack>
            )}
          </Stack>
        )}

        {filtered.length === 0 ? (
          <Stack spacing={0.75} sx={{ alignItems: 'flex-start' }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>Nothing matches those filters.</Typography>
            <Button
              size="small"
              onClick={() => {
                setQuery('');
                 setActiveTags([]);
                setOwner('all');
              }}
              sx={{ fontSize: '0.72rem', color: C.cyan }}
            >
              Clear all filters
            </Button>
          </Stack>
        ) : (
          /* Two cards to a row on a normal desktop, one on a phone. Not a
             fixed column count: this panel also has to survive a narrow
             window, and `auto-fill` lets the cards decide rather than the
             viewport breakpoint guessing for them. */
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 1.25,
              alignItems: 'start',
            }}
          >
            {filtered.map((f, i) => (
              <FittingCard
                key={f.id}
                fitting={f}
                index={i}
                selected={selectedId === f.id}
                justImported={justImportedId === f.id}
                onSelect={() => onSelect(f.id)}
                onDelete={() => onDelete(f.id)}
              />
            ))}
          </Box>
        )}
      </Stack>
    </ConsolePanel>
  );
};

/** Whose fits to show. The page lists everything you can SEE by default —
 * your own plus every shared one — because "what does the corp fly here" is
 * the question it exists to answer; "mine" is the narrowing, not the base. */
type OwnerFilter = 'all' | 'mine' | 'shared';
const OWNER_FILTERS: { value: OwnerFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'shared', label: 'Shared with me' },
];

/** One filter pill. Extracted because the tag row, the category row and the
 * owner row are the same control and were drifting apart as three copies. */
const FilterChip: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <Chip
    label={label}
    size="small"
    onClick={onClick}
    sx={{
      bgcolor: active ? hexToRgba(C.cyan, 0.12) : 'transparent',
      color: active ? C.cyan : C.textDim,
      border: `1px solid ${active ? C.cyan : C.borderSub}`,
      fontFamily: FONT.mono,
      cursor: 'pointer',
      transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': { borderColor: hexToRgba(C.cyan, 0.6) },
    }}
  />
);

/** How to order the grid. Tank and damage are here because they are the
 * comparison the page exists to serve; a fit with no computed stats sorts
 * last on those rather than as a zero, which would claim it has none. */
type SortKey = 'recent' | 'name' | 'ehp' | 'dps';
const SORTS: Record<SortKey, { label: string; compare: (a: FittingSummary, b: FittingSummary) => number }> = {
  recent: { label: 'Newest first', compare: (a, b) => b.createdAt.localeCompare(a.createdAt) },
  name: { label: 'Name', compare: (a, b) => a.name.localeCompare(b.name) },
  ehp: { label: 'Most EHP', compare: (a, b) => (b.stats?.tank.totalEhp ?? -1) - (a.stats?.tank.totalEhp ?? -1) },
  dps: { label: 'Most DPS', compare: (a, b) => (b.stats?.offense.totalDps ?? -1) - (a.stats?.offense.totalDps ?? -1) },
};

/** Stagger ceiling for the grid's own entrance — past this it reads as
 * loading rather than a filter settling into place. */
const ROW_STAGGER_MS = 35;
const ROW_MAX_DELAY_MS = 280;

/**
 * One fit, previewed.
 *
 * This was a one-line row: name, ship, tags. Which meant the page could
 * tell you a fit EXISTED and nothing about whether you wanted it — every
 * "is this the one" required opening the dialog, and comparing two required
 * opening both and remembering the first. The card carries the four facts
 * that answer it without a click: the hull (recognised by silhouette faster
 * than by name), the tank with its resist profile, the damage, and how it
 * is put together. The dialog still holds the full fit — module list, EFT,
 * price — which is a different question.
 */
const FittingCard: React.FC<{
  fitting: FittingSummary;
  index: number;
  selected: boolean;
  justImported: boolean;
  onSelect: () => void;
  onDelete: () => void;
}> = ({ fitting: f, index, selected, justImported, onSelect, onDelete }) => {
  const { me } = useAuth();
  const reduced = prefersReducedMotion();
  const delay = Math.min(index * ROW_STAGGER_MS, ROW_MAX_DELAY_MS);
  const stats = f.stats;
  const canDelete = f.isMine || me.isAdmin;

  return (
    <Stack
      spacing={1.25}
      onClick={onSelect}
      sx={{
        ...frameSx(selected ? C.blue : C.cyan),
        ...(justImported ? justImportedSx() : {}),
        p: 1.5,
        height: '100%',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, border-color 0.2s ease',
        '&:hover': { transform: 'translateY(-1px)' },
        animation: reduced ? 'none' : `fitRowIn 0.32s cubic-bezier(0.16,1,0.3,1) ${delay}ms both`,
        '@keyframes fitRowIn': {
          '0%': { opacity: 0, transform: 'translateY(6px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Header: hull portrait, then who it belongs to and how far it
          reaches. The delete stays top-right, out of the stats' way. */}
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <ShipPortrait typeId={f.shipTypeId} name={f.shipName} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: C.text, fontWeight: 600, lineHeight: 1.25 }} noWrap title={f.name}>
            {f.name}
          </Typography>
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.76rem' }} noWrap>
            {f.shipName}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, rowGap: 0.5 }}>
            {/* Fits are no longer account-private by default-and-forever: a
                shared one shows whose doctrine it is, your own shows how far
                it reaches. */}
            {f.isMine ? (
              f.shareScope !== 'private' && <StatusBadge label={f.shareScope} accent={C.wormhole} />
            ) : (
              <StatusBadge label={f.ownerName ?? 'shared'} accent={C.textDim} dim />
            )}
            {/* Only once a fit is tagged does its maturity start mattering to
                anyone else — an untagged personal scratch fit is nobody's
                suggestion, so the badge would be noise on it. */}
            {f.tags.length > 0 && <FittingStatusBadge status={f.status} />}
          </Stack>
        </Box>
        {/* Deleting someone else's shared doctrine is the owner's call or an
            admin's — the server enforces the same rule (DELETE is owner- or
            admin-scoped). */}
        {canDelete && (
          <IconButton
            size="small"
            aria-label={`Delete ${f.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{ color: C.textDim, flexShrink: 0 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {!stats ? (
        <Typography sx={{ color: C.textDim, fontSize: '0.76rem' }}>
          Hull didn&rsquo;t resolve via ESI &mdash; no computed stats for this one.
        </Typography>
      ) : (
        <>
          {/* The headline pair. EHP and DPS are the two numbers anyone
              actually compares fits on, so they get size and colour; the
              rest of the card explains them. */}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <HeadlineStat label="ehp" value={Math.round(stats.tank.totalEhp).toLocaleString()} accent={C.cyan} />
            <HeadlineStat label="dps" value={Math.round(stats.offense.totalDps).toLocaleString()} accent={C.red} />
            <HeadlineStat
              label="m/s"
              value={Math.round(stats.navigation.mwdVelocity ?? stats.navigation.maxVelocity).toLocaleString()}
              accent={C.gold}
              hint={stats.navigation.mwdVelocity ? 'MWD overheat-off velocity' : 'unpropelled velocity'}
            />
            <HeadlineStat label="sig" value={`${Math.round(stats.navigation.signatureRadius)}m`} accent={C.neutral} />
          </Stack>

          {/* The three layers with their resist profile — the same meter the
              dialog uses, so a number read here means the same thing there. */}
          <Stack spacing={0.75}>
            <EhpLayer label="Shield" layer={stats.tank.shield} accent={C.cyan} />
            <EhpLayer label="Armor" layer={stats.tank.armor} accent={C.gold} />
            <EhpLayer label="Hull" layer={stats.tank.hull} accent={C.red} />
          </Stack>

          <FitShape stats={stats} />
        </>
      )}

      {f.tags.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5, mt: 'auto' }}>
          {f.tags.map((t) => (
            <StatusBadge
              key={`${t.tag_type}-${t.tag_value}-${t.recommendation}`}
              label={t.recommendation === 'minimum' ? `${t.tag_value} (min)` : t.tag_value}
              accent={t.recommendation === 'minimum' ? C.gold : C.cyan}
              // A proposal from someone other than the owner/an admin isn't
              // live until a second member endorses it — greyed here rather
              // than hidden, so it's visible as "proposed" without reading
              // as a settled recommendation. See FittingTagEditor for the
              // full endorsement flow.
              dim={!t.approved}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

/** The hull, as EVE draws it. A render rather than the flat icon: the
 * silhouette is what someone recognises before they have read anything, and
 * that is the whole job of the top-left corner of a card. Falls back to
 * nothing at all rather than a broken-image glyph. */
const ShipPortrait: React.FC<{ typeId: number | null; name: string }> = ({ typeId, name }) => {
  const [failed, setFailed] = useState(false);
  if (!typeId || failed) {
    return (
      <Box
        sx={{
          width: 64,
          height: 64,
          flexShrink: 0,
          border: `1px solid ${C.hairline}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.6rem', color: C.textDim }}>no img</Typography>
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={`https://images.evetech.net/types/${typeId}/render?size=128`}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{ width: 64, height: 64, flexShrink: 0, border: `1px solid ${C.hairline}`, objectFit: 'cover' }}
    />
  );
};

const HeadlineStat: React.FC<{ label: string; value: string; accent: string; hint?: string }> = ({
  label,
  value,
  accent,
  hint,
}) => (
  <Tooltip title={hint ?? ''} disableHoverListener={!hint}>
    <Box sx={{ minWidth: 0, cursor: hint ? 'help' : 'inherit' }}>
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '1.02rem', color: accent, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.55rem') }}>{label}</Typography>
    </Box>
  </Tooltip>
);

/** How the fit is put together, in one line: what it fills, what it shoots
 * with, and whether it brings EWAR. Not a module list — that is the
 * dialog's job — but enough to tell a shield brick from a neut boat without
 * opening either. */
const FitShape: React.FC<{ stats: NonNullable<FittingSummary['stats']> }> = ({ stats }) => {
  const { slots, offense, ewar } = stats;
  const weapons = [
    offense.turretCount > 0 ? `${offense.turretCount} turret${offense.turretCount === 1 ? '' : 's'}` : null,
    offense.launcherCount > 0 ? `${offense.launcherCount} launcher${offense.launcherCount === 1 ? '' : 's'}` : null,
    offense.droneDps > 0 ? 'drones' : null,
  ].filter(Boolean);
  // EWAR counts, not strengths: "does this bring neuts" is the card-level
  // question, "how much neut" is the dialog's.
  const ewarBits = [
    ewar.neutCount > 0 ? `${ewar.neutCount} neut` : null,
    ewar.webCount > 0 ? `${ewar.webCount} web` : null,
    ewar.tpCount > 0 ? `${ewar.tpCount} paint` : null,
  ].filter(Boolean);

  return (
    <Stack spacing={0.25}>
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.textDim }}>
        {slots.high}H / {slots.mid}M / {slots.low}L / {slots.rig}R
        {weapons.length > 0 && <> &middot; {weapons.join(', ')}</>}
      </Typography>
      {ewarBits.length > 0 && (
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.gold }}>
          {ewarBits.join(' · ')}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * The fitting detail used to render inline at the bottom of the page — pick
 * a fitting from a long saved list and the numbers you actually wanted
 * appeared below the fold, off-screen, with no visual link back to what you
 * clicked. A modal keeps the click and its result in the same place, and
 * gives the fit somewhere to carry its own actions (Copy EFT, Close)
 * instead of borrowing the page header's.
 */
const FittingDialog: React.FC<{ id: number | null; onClose: () => void }> = ({ id, onClose }) => {
  const { me } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['fitting', id],
    queryFn: () => fetchFitting(id!),
    enabled: id !== null,
  });
  const [copied, setCopied] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const queryClient = useQueryClient();

  // Sharing lives here rather than on the row: 'group' needs a second
  // choice, and the dialog is the one place with room for it.
  const shareMutation = useMutation({
    mutationFn: (v: { scope: ShareScope; groupId: number | null }) => updateFittingShare(id!, v.scope, v.groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fitting', id] });
      queryClient.invalidateQueries({ queryKey: ['fittings'] });
      queryClient.invalidateQueries({ queryKey: ['fitting-recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['fittings-by-class'] });
      queryClient.invalidateQueries({ queryKey: ['fittings-by-site'] });
    },
  });

  // Separate mutation from sharing on purpose: who may SEE a fit and how far
  // along it is are unrelated questions, and the PATCH takes either alone.
  const statusMutation = useMutation({
    mutationFn: (status: FittingStatus | null) => updateFittingStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fitting', id] });
      queryClient.invalidateQueries({ queryKey: ['fittings'] });
    },
  });

  const copyEft = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.eftRaw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard access denied (permissions, non-secure context) — the raw
      // EFT is still visible/selectable inside the panel below, so nothing
      // is actually lost, just the one-click convenience.
    }
  };

  // Whoever can actually change sharing/status — the fitting's owner, or an
  // admin covering for one. Everyone else still sees where a fit stands
  // (StatusBadge in the header), just not the controls to change it.
  const canManage = !!data && (data.isMine || me.isAdmin);

  return (
    <Dialog open={id !== null} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT.display, fontWeight: 700, fontSize: '1.05rem', color: C.text }} noWrap>
            {data ? `${data.name} — ${data.shipName}` : 'Loading…'}
          </Typography>
          {/* Read-only for everyone, including the owner — the dropdown is
              where that actually gets changed now, this is just "what does
              it currently say". */}
          {data && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
              {data.shareScope !== 'private' && <StatusBadge label={data.shareScope} accent={C.wormhole} />}
              <FittingStatusBadge status={data.status} />
            </Stack>
          )}
        </Box>
        {data && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
            onClick={copyEft}
            sx={{ flexShrink: 0 }}
          >
            {copied ? 'Copied!' : 'Copy EFT'}
          </Button>
        )}
        {/* Sharing and status used to sit in the body, above the fit itself
            — real estate spent on a control most viewers never touch, ahead
            of the numbers everyone opens the dialog FOR. A dropdown off the
            title bar keeps it one click away without taxing the layout for
            it every time. */}
        {canManage && (
          <>
            <IconButton
              size="small"
              onClick={(e) => setSettingsAnchor(e.currentTarget)}
              sx={{ color: C.textDim, flexShrink: 0 }}
              aria-label="Sharing and status"
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
            <Popover
              open={!!settingsAnchor}
              anchorEl={settingsAnchor}
              onClose={() => setSettingsAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Stack spacing={1.5} sx={{ p: 2, width: 280 }}>
                <ShareScopeSelect
                  value={data!.shareScope}
                  groupId={data!.shareGroupId}
                  onChange={(scope, groupId) => shareMutation.mutate({ scope, groupId })}
                />
                <StatusPicker
                  value={data!.status}
                  busy={statusMutation.isPending}
                  onChange={(next) => statusMutation.mutate(next)}
                />
              </Stack>
            </Popover>
          </>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: C.textDim, flexShrink: 0 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: C.hairline }}>
        {isLoading || !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: C.blue }} />
          </Box>
        ) : (
          <>
            {/* Tagging is a member action now, not an admin one — so it
                lives on the fit itself, where the person who flew it is
                already looking, rather than on a separate admin screen. */}
            <Box sx={{ mb: 2.5 }}>
              <FittingTagEditor fittingId={data.id} tags={data.tags} isFittingOwner={data.isMine} />
            </Box>
            <FittingStatsBody fitting={data} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Where this fit stands, set by its owner.
 *
 * Segmented rather than a dropdown: there are three values plus a clear, the
 * set never grows, and the whole point is that the current one is readable
 * without opening anything — a select hides the answer behind a click on the
 * one screen that exists to give it. Clicking the active value clears it back
 * to unrated, so a claim made by accident can be taken back.
 *
 * Only these three exist because they are the only distinctions that change
 * what a reader does: don't undock it, expect surprises, or fly it. */
const STATUS_CHOICES: { value: FittingStatus; label: string; accent: string; hint: string }[] = [
  { value: 'wip', label: 'WIP', accent: C.red, hint: 'Still being built — not for undocking.' },
  { value: 'testing', label: 'testing', accent: C.gold, hint: 'Being flown and judged.' },
  { value: 'working', label: 'working', accent: C.green, hint: 'Proven. Safe to suggest to the corp.' },
];

const StatusPicker: React.FC<{
  value: FittingStatus | null;
  busy: boolean;
  onChange: (next: FittingStatus | null) => void;
}> = ({ value, busy, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
    <Typography sx={{ color: C.textDim, fontSize: '0.8rem', flexShrink: 0 }}>Status</Typography>
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {STATUS_CHOICES.map((choice) => {
        const active = value === choice.value;
        return (
          <Tooltip key={choice.value} title={active ? `${choice.hint} Click to clear.` : choice.hint}>
            <Box
              component="button"
              type="button"
              disabled={busy}
              aria-pressed={active}
              onClick={() => onChange(active ? null : choice.value)}
              sx={{
                px: 1.25,
                py: 0.4,
                borderRadius: '999px',
                cursor: busy ? 'wait' : 'pointer',
                fontFamily: FONT.mono,
                fontSize: '0.72rem',
                letterSpacing: '0.04em',
                color: active ? choice.accent : C.textDim,
                backgroundColor: active ? hexToRgba(choice.accent, 0.12) : 'transparent',
                border: `1px solid ${active ? hexToRgba(choice.accent, 0.55) : C.hairline}`,
                outline: 'none',
                transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
                '&:hover:not(:disabled)': { borderColor: hexToRgba(choice.accent, 0.7), color: choice.accent },
                '&:focus-visible': { borderColor: C.cyan, boxShadow: `0 0 0 2px ${hexToRgba(C.cyan, 0.4)}` },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              {choice.label}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
    {!value && (
      <Typography sx={{ color: C.textDim, fontSize: '0.72rem', opacity: 0.8 }}>
        unrated &mdash; shown as such wherever this fit is suggested
      </Typography>
    )}
  </Stack>
);

/** Reusable for both the Fittings popup (below) and (later) a "Recommended
 * Fittings" strip on site detail pages. Kept as a thin ConsolePanel wrapper
 * around FittingStatsBody so a future standalone usage still gets a
 * self-titled panel; the popup uses the body directly since DialogTitle
 * already carries the name. */
export const FittingStatsPanel: React.FC<{ fitting: FittingDetail }> = ({ fitting }) => (
  <ConsolePanel title={`${fitting.name} — ${fitting.shipName}`} accent={C.blue}>
    <FittingStatsBody fitting={fitting} />
  </ConsolePanel>
);

const FittingStatsBody: React.FC<{ fitting: FittingDetail }> = ({ fitting }) => {
  const stats = fitting.stats;

  return (
    <>
      {/* The hull and the one money number, together, first — an FC asks
          "what is this / what does it cost to replace" before anything
          else. Both used to live inside the workbench grid below (the hull
          as a second column beside the slots, the cost as a third beside
          both), which buried them in the middle of module detail instead of
          answering first. */}
      <Box sx={{ mb: 2 }}>
        <FittingOverviewBox shipTypeId={fitting.shipTypeId} shipName={fitting.shipName} isk={fitting.isk} />
      </Box>

      {!stats ? (
        <>
          <FittingWorkbench fitting={fitting} />
          <Typography sx={{ color: C.textDim, mt: 2 }}>
            Ship type couldn&rsquo;t be resolved via ESI — computed stats unavailable, but the fitting is still saved.
          </Typography>
        </>
      ) : (
        <>
          {/* Slots beside Defense/Offense: the module list on the left, and
              on the right — in the width the hull render used to occupy —
              the two numbers that module list produces. Reading left to
              right is "what's fitted" then "what it does". */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1fr)' }, gap: 2, alignItems: 'start' }}>
            <FittingWorkbench fitting={fitting} />
            <Stack spacing={1.5}>
              <StatBox title="Defense" accent={C.cyan} value={`${Math.round(stats.tank.totalEhp).toLocaleString()} EHP`}>
                <Stack spacing={1}>
                  <EhpLayer label="Shield" layer={stats.tank.shield} accent={C.cyan} />
                  <EhpLayer label="Armor" layer={stats.tank.armor} accent={C.gold} />
                  <EhpLayer label="Hull" layer={stats.tank.hull} accent={C.red} />
                </Stack>
              </StatBox>

              <StatBox title="Offense" accent={C.red} value={`${Math.round(stats.offense.totalDps).toLocaleString()} DPS`}>
                <Stack spacing={0.5}>
                  {stats.offense.turretDps > 0 && (
                    <StatRow label={`Turrets (${stats.offense.turretAmmoName ?? 'no ammo'})`} value={`${Math.round(stats.offense.turretDps)} dps`} />
                  )}
                  {stats.offense.launcherDps > 0 && (
                    <StatRow label={`Launchers (${stats.offense.launcherAmmoName ?? 'no ammo'})`} value={`${Math.round(stats.offense.launcherDps)} dps`} />
                  )}
                  {stats.offense.droneDps > 0 && <StatRow label="Drones" value={`${Math.round(stats.offense.droneDps)} dps`} />}
                  {stats.offense.totalDps === 0 && <Typography sx={{ color: C.textDim, fontSize: '0.82rem' }}>No weapons/ammo detected.</Typography>}
                </Stack>
              </StatBox>
            </Stack>
          </Box>

          {/* Navigation and Capacitor below both — supporting numbers, not
              headline ones, so they sit under the fit and its two combat
              stats rather than competing with them for the top row. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
            <StatBox title="Navigation" accent={C.gold}>
              <Stack spacing={0.5}>
                <StatRow label="Velocity" value={`${stats.navigation.maxVelocity} m/s`} />
                {stats.navigation.mwdVelocity && <StatRow label="MWD Velocity" value={`${stats.navigation.mwdVelocity} m/s`} />}
                {stats.navigation.abVelocity && <StatRow label="AB Velocity" value={`${stats.navigation.abVelocity} m/s`} />}
                <StatRow label="Signature Radius" value={`${stats.navigation.signatureRadius} m`} />
                {stats.navigation.alignTime && <StatRow label="Align Time" value={`${stats.navigation.alignTime}s`} />}
              </Stack>
            </StatBox>

            <StatBox title="Capacitor & Targeting" accent={C.blue}>
              <Stack spacing={0.5}>
                <StatRow label="Cap Capacity" value={`${Math.round(stats.capacitor.capacity)} GJ`} />
                <StatRow label="Cap Recharge" value={`${(stats.capacitor.rechargeTime / 1000).toFixed(0)}s`} />
                <StatRow label="Target Range" value={`${Math.round(stats.targeting.maxTargetRange / 1000)} km`} />
                <StatRow label="Max Locked Targets" value={`${stats.targeting.maxLockedTargets}`} />
              </Stack>
            </StatBox>
          </Box>
        </>
      )}

      {/* Says what the numbers assume, right under them. Without this a
          reader can't tell whether a figure is achievable or aspirational —
          and every stat above is the all-V figure, not theirs. */}
      {stats && (
        <Typography sx={{ color: hexToRgba(C.neutral, 0.85), fontFamily: FONT.mono, fontSize: '0.66rem', mt: 2 }}>
          All skills at level V (pyfa default). Your own numbers will be lower until your skills catch up.
        </Typography>
      )}
    </>
  );
};

/** One boxed stat group — Defense, Offense, Navigation, or Capacitor &
 * Targeting. Previously these four were bare label+Stack pairs floating in
 * a grid with no border of their own, the one place on this page that
 * wasn't boxed the way every other panel in the app is. Same hairline frame
 * as the cost/overview box above them, at a consistent size, so the four
 * read as siblings rather than four different levels of finish. */
const StatBox: React.FC<{ title: string; accent: string; value?: string; children: React.ReactNode }> = ({
  title,
  accent,
  value,
  children,
}) => (
  <Box sx={{ ...frameSx(accent), p: 1.5 }}>
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography
        sx={{ color: C.textDim, fontFamily: FONT.sub, fontWeight: 700, letterSpacing: 1.5, fontSize: '0.68rem', textTransform: 'uppercase' }}
      >
        {title}
      </Typography>
      {value && <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: accent }}>{value}</Typography>}
    </Stack>
    {children}
  </Box>
);

const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: C.textDim }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: C.text }}>{value}</Typography>
  </Stack>
);

/** HP/resist meter — a thin filled indicator bar, same idiom as
 * SystemIntelPanel's ActivityHourBars (small data-viz bars are the one
 * established exception to the zero-fill panel rule; this isn't a panel or
 * badge, it's a chart mark). */
const EhpLayer: React.FC<{
  label: string;
  layer: { hp: number; ehp: number; em: number; th: number; kin: number; exp: number };
  accent: string;
}> = ({ label, layer, accent }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between">
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: C.text }}>{label}</Typography>
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: accent }}>
        {Math.round(layer.hp).toLocaleString()} hp / {Math.round(layer.ehp).toLocaleString()} ehp
      </Typography>
    </Stack>
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
      {(['em', 'th', 'kin', 'exp'] as const).map((k) => (
        <Box key={k} sx={{ flex: 1 }}>
          <Box sx={{ height: 3, borderRadius: '1px', bgcolor: C.borderSub, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', inset: 0, width: `${Math.max(0, Math.min(100, layer[k]))}%`, bgcolor: hexToRgba(accent, 0.65) }} />
          </Box>
          <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.6rem', color: C.textDim, textAlign: 'center' }}>
            {k} {layer[k]}%
          </Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);

export default Fittings;
